import { zodResolver } from "@hookform/resolvers/zod";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Select from "@mui/material/Select";
import { ReactNode } from "react";
import {
  Control,
  Controller,
  FieldErrorsImpl,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { z } from "zod";
import { RouterOutput, trpc } from "#next/utils/trpc";

import {
  SavedSearchSubscriptionFrequency,
  SavedSearchSubscriptionIncludedResultTypes,
} from "@prisma-postgres";
import { uniqBy } from "lodash";
import {
  DisplayNameForSubscriptionFrequency,
  DisplayNameForSubscriptionIncludedResultType,
} from "#next/utils/shared";
import {
  FormModal,
  FormModalControls,
  FormModalTitle,
  FormModalWithMessage,
} from "#next/components/modals/FormModal";
import { trackAction } from "#next/utils/trackUserActions";
import { RenderQueryFormModal } from "#next/components/modals/RenderQueryFormModal";
import { TSavedSearch } from "#next/components/catalog/base/shared";

type TGetSavedSearch = RouterOutput["savedSearch"]["getById"];

const Option = ({
  beforeText,
  afterText,
  children,
}: {
  beforeText: string;
  afterText?: string;
  children: ReactNode;
}) => {
  return (
    <Grid item sx={{ my: 1 }}>
      <Grid container alignItems="center">
        <Grid item>
          <Typography>{beforeText}</Typography>
        </Grid>
        <Grid item sx={{ mx: 1 }}>
          {children}
        </Grid>
        {afterText && (
          <Grid>
            <Typography>{afterText}</Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const FormUI = ({
  searches,
  control,
  errors,
  showSearchSelector,
}: {
  control: Control<TFormValues>;
  errors: FieldErrorsImpl<TFormValues>;
  searches: TGetSavedSearch[] | TSavedSearch[];
  showSearchSelector: boolean;
}) => {
  const emailFields = useFieldArray({
    name: "emails",
    control,
    rules: { minLength: 4 },
  });

  return (
    <Grid container direction="column" sx={{ minWidth: "550px" }}>
      <Grid container>
        {showSearchSelector && (
          <Option beforeText="For the project" afterText="">
            <FormControl>
              <Controller
                control={control}
                name="savedSearchId"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <Select
                      labelId="select-search"
                      size="small"
                      value={value}
                      onChange={(...args) => {
                        trackAction("Subscribe > Change Project");
                        return onChange(...args);
                      }}
                      onBlur={onBlur}
                    >
                      {searches.map((search) => (
                        <MenuItem value={search.id} key={search.id}>
                          {search.name}
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              />
            </FormControl>
          </Option>
        )}
        <Option beforeText={`${showSearchSelector ? "s" : "S"}end updates`}>
          <FormControl>
            <Controller
              control={control}
              name="frequency"
              render={({ field: { onChange, onBlur, value } }) => {
                const Frequency = SavedSearchSubscriptionFrequency;
                return (
                  <Select
                    size="small"
                    value={value}
                    onChange={(...args) => {
                      trackAction("Subscribe > Change Frequency");
                      return onChange(...args);
                    }}
                    onBlur={onBlur}
                  >
                    <MenuItem value={Frequency.Immediately}>
                      {
                        DisplayNameForSubscriptionFrequency[
                          Frequency.Immediately
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={Frequency.EveryThreeHours}>
                      {
                        DisplayNameForSubscriptionFrequency[
                          Frequency.EveryThreeHours
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={Frequency.TwicePerDay}>
                      {
                        DisplayNameForSubscriptionFrequency[
                          Frequency.TwicePerDay
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={Frequency.Daily}>
                      {DisplayNameForSubscriptionFrequency[Frequency.Daily]}
                    </MenuItem>
                    <MenuItem value={Frequency.TwicePerWeek}>
                      {
                        DisplayNameForSubscriptionFrequency[
                          Frequency.TwicePerWeek
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={Frequency.Weekly}>
                      {DisplayNameForSubscriptionFrequency[Frequency.Weekly]}
                    </MenuItem>
                    <MenuItem value={Frequency.Monthly}>
                      {DisplayNameForSubscriptionFrequency[Frequency.Monthly]}
                    </MenuItem>
                  </Select>
                );
              }}
            />
          </FormControl>
        </Option>
        <Option beforeText="and include" afterText="results">
          <FormControl>
            <Controller
              control={control}
              name="condition"
              render={({ field: { onChange, onBlur, value } }) => {
                const IncludedResultTypes =
                  SavedSearchSubscriptionIncludedResultTypes;

                return (
                  <Select
                    size="small"
                    value={value}
                    onChange={(...args) => {
                      trackAction("Subscribe > Change Include");
                      return onChange(...args);
                    }}
                    onBlur={onBlur}
                  >
                    <MenuItem value={IncludedResultTypes.All}>
                      {
                        DisplayNameForSubscriptionIncludedResultType[
                          IncludedResultTypes.All
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={IncludedResultTypes.HighlyHateful}>
                      {
                        DisplayNameForSubscriptionIncludedResultType[
                          IncludedResultTypes.HighlyHateful
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={IncludedResultTypes.HighlyOffensive}>
                      {
                        DisplayNameForSubscriptionIncludedResultType[
                          IncludedResultTypes.HighlyOffensive
                        ]
                      }
                    </MenuItem>
                    <MenuItem value={IncludedResultTypes.HighlyViolent}>
                      {
                        DisplayNameForSubscriptionIncludedResultType[
                          IncludedResultTypes.HighlyViolent
                        ]
                      }
                    </MenuItem>
                  </Select>
                );
              }}
            />
          </FormControl>
        </Option>
      </Grid>
      <Grid item container direction="column" alignItems="start">
        <Typography sx={{ my: 2 }}>Send update emails to</Typography>

        {emailFields.fields.map((field, number) => {
          const error = errors.emails
            ? errors.emails[number]?.address?.message
            : undefined;
          return (
            <FormControl key={field.id} sx={{ mb: 2 }}>
              <Controller
                name={`emails.${number}.address`}
                control={control}
                render={(data) => {
                  return (
                    <Grid container>
                      <Grid item>
                        <TextField
                          size="small"
                          defaultValue={data.field.value}
                          onChange={data.field.onChange}
                          sx={{ width: "30rem" }}
                          onBlur={data.field.onBlur}
                          error={!!error}
                          helperText={error ?? ""}
                        />
                      </Grid>
                      {number > 0 && (
                        <Grid item>
                          <IconButton
                            onClick={() => {
                              trackAction("Subscribe > Remove Email");
                              emailFields.remove(number);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  );
                }}
              />
            </FormControl>
          );
        })}
        {errors.emails?.type === "duplicates" && (
          <Typography color="error" sx={{ mb: 2 }}>
            Cannot have duplicate email addresses
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            trackAction("Subscribe > Add Email");
            emailFields.append({ address: "" });
          }}
        >
          Add Another Email Address
        </Button>
      </Grid>
    </Grid>
  );
};

type TFormValues = {
  frequency: SavedSearchSubscriptionFrequency;
  condition: SavedSearchSubscriptionIncludedResultTypes;
  emails: { address: string }[];
  savedSearchId: string;
};

const createSubscriptionTitle = "Subscribe to Project Updates";
const editSubscriptionTitle = "Edit Subscription";

const getZForm = (searches: TGetSavedSearch[] | TSavedSearch[]) =>
  z.object({
    frequency: z.nativeEnum(SavedSearchSubscriptionFrequency),
    condition: z.nativeEnum(SavedSearchSubscriptionIncludedResultTypes),
    savedSearchId: z
      .string()
      .uuid()
      .refine((val) => searches.map((search) => search.id).includes(val)),
    emails: z.array(z.object({ address: z.string().email() })),
  });

const SubscriptionForm = ({
  searches,
  onClose,
  open,
  showSearchSelector,
  saveButtonName,
  title,
  mutate,
  defaultValues,
}: {
  searches: TGetSavedSearch[] | TSavedSearch[];
  onClose: (didSave: boolean) => void;
  open: boolean;
  showSearchSelector: boolean;
  saveButtonName: "Subscribe" | "Update";
  title: string;
  mutate: (data: TFormValues) => Promise<void>;
  defaultValues: z.infer<ReturnType<typeof getZForm>>;
}) => {
  const zForm = getZForm(searches);
  zForm.parse(defaultValues);

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    setError,
    clearErrors,
  } = useForm<TFormValues>({
    defaultValues,
    resolver: zodResolver(zForm),
    mode: "onBlur",
  });

  // Custom validation to check that we don't have duplicate email addresses.
  // Annoyingly I could not get this work as part of the zod schema (using .refine)
  // because react-hook-form doesn't seem to consistently validate when useFieldArray
  // is being used? I would see the error being set, but not unsetting
  // after the form became valid, for example.
  // Pretty sure it's not an issue with Zod or how I did that part, seems to be RHF
  // related. To RHF's credit it makes the workaround pretty easy to do.
  // See "isValid is sometimes false despite no errors"
  // https://github.com/react-hook-form/react-hook-form/issues/2755
  watch(({ emails }) => {
    if (!emails) {
      return;
    }
    const unique = uniqBy(emails, "address");
    if (unique.length !== emails.length) {
      setError("emails", {
        type: "duplicates",
      });
    } else {
      clearErrors("emails");
    }
  });

  return (
    <FormModal open={open} onClose={() => onClose(false)}>
      <Grid container direction="column">
        <FormModalTitle title={title} />
        <Grid item>
          <FormUI
            showSearchSelector={showSearchSelector}
            control={control}
            errors={errors}
            searches={searches}
          />
        </Grid>
      </Grid>
      <FormModalControls
        onClose={() => onClose(false)}
        onSave={handleSubmit(async (data) => {
          trackAction(`Subscribe > ${saveButtonName}`);
          await mutate(data);
          onClose(true);
        })}
        saveIsDisabled={!isValid}
        saveButtonName={saveButtonName}
      ></FormModalControls>
    </FormModal>
  );
};

export const CreateSubscriptionWithSearchSelectorModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (didSave: boolean) => void;
}) => {
  const closedWithoutSaving = () => onClose(false);

  const utils = trpc.useContext();
  const createMutation = trpc.searchSubscription.create.useMutation({
    onSuccess: (data) => {
      utils.searchSubscription.getById.setData({ id: data.id }, data);
      utils.searchSubscription.list.invalidate();
    },
  });

  return (
    <RenderQueryFormModal
      title={createSubscriptionTitle}
      open={open}
      onClose={onClose}
      useQuery={[trpc.savedSearch.list, trpc.user.me]}
      render={(searchData, userData) => {
        const searches = searchData.ownedByUser;
        const firstSearch = searches[0];
        if (!firstSearch) {
          return (
            <FormModalWithMessage
              open={open}
              onClose={closedWithoutSaving}
              title={createSubscriptionTitle}
              message="Please create a Project first"
            />
          );
        }
        return (
          <SubscriptionForm
            showSearchSelector={true}
            searches={searches}
            defaultValues={{
              condition: SavedSearchSubscriptionIncludedResultTypes.All,
              frequency: SavedSearchSubscriptionFrequency.Daily,
              savedSearchId: firstSearch.id,
              emails: [{ address: userData.email }],
            }}
            open={open}
            onClose={onClose}
            saveButtonName="Subscribe"
            title={createSubscriptionTitle}
            mutate={async (data) =>
              createMutation.mutate({
                frequency: data.frequency,
                includeResults: data.condition,
                savedSearchId: data.savedSearchId,
                sendToEmails: data.emails.map((email) => email.address),
              })
            }
          />
        );
      }}
    />
  );
};

export const CreateSubscriptionModal = ({
  savedSearchId,
  open,
  onClose,
}: {
  savedSearchId: string;
  open: boolean;
  onClose: (didSave: boolean) => void;
}) => {
  const utils = trpc.useContext();
  const createMutation = trpc.searchSubscription.create.useMutation({
    onSuccess: (data) => {
      utils.searchSubscription.getById.setData({ id: data.id }, data);
      utils.searchSubscription.list.invalidate();
    },
  });

  return (
    <RenderQueryFormModal
      title={createSubscriptionTitle}
      open={open}
      onClose={onClose}
      useQuery={[trpc.savedSearch.getById, trpc.user.me]}
      input={[{ savedSearchId }, undefined]}
      render={(searchData, userData) => (
        <SubscriptionForm
          showSearchSelector={false}
          searches={[searchData]}
          open={open}
          onClose={onClose}
          saveButtonName="Subscribe"
          title={createSubscriptionTitle}
          defaultValues={{
            condition: SavedSearchSubscriptionIncludedResultTypes.All,
            frequency: SavedSearchSubscriptionFrequency.Daily,
            savedSearchId: savedSearchId,
            emails: [{ address: userData.email }],
          }}
          mutate={async (data) =>
            createMutation.mutate({
              frequency: data.frequency,
              includeResults: data.condition,
              savedSearchId: data.savedSearchId,
              sendToEmails: data.emails.map((email) => email.address),
            })
          }
        />
      )}
    />
  );
};

export const EditSubscriptionModal = ({
  subscriptionId,
  open,
  onClose,
}: {
  subscriptionId: string;
  open: boolean;
  onClose: (didSave: boolean) => void;
}) => {
  const utils = trpc.useContext();
  const updateMutation = trpc.searchSubscription.update.useMutation({
    onSuccess: (data) => {
      utils.searchSubscription.getById.setData({ id: data.id }, data);
      utils.searchSubscription.list.invalidate();
    },
  });

  return (
    <RenderQueryFormModal
      title={editSubscriptionTitle}
      open={open}
      onClose={onClose}
      useQuery={[trpc.searchSubscription.getById, trpc.savedSearch.list]}
      input={[{ id: subscriptionId }, undefined]}
      render={(subscription, savedSearches) => (
        <SubscriptionForm
          showSearchSelector={true}
          searches={savedSearches.ownedByUser}
          open={open}
          onClose={onClose}
          saveButtonName="Update"
          title={editSubscriptionTitle}
          mutate={async (data) =>
            updateMutation.mutate({
              id: subscriptionId,
              frequency: data.frequency,
              includeResults: data.condition,
              savedSearchId: data.savedSearchId,
              sendToEmails: data.emails.map((email) => email.address),
            })
          }
          defaultValues={{
            condition: subscription.includeResults,
            emails: subscription.sendToEmails.map((email: string) => ({
              address: email,
            })),
            frequency: subscription.frequency,
            savedSearchId: subscription.savedSearchId,
          }}
        />
      )}
    />
  );
};
